import type {
  Beat,
  CameraWaypoint,
  DecisionDrillConfig,
  PrivacyClassifyConfig,
  PrivacyHuntConfig,
  ScenarioSpec,
  ScoringEvent,
  SlideDeckConfig,
  SlideDecision,
  SlideVisualKind,
} from "./types";
import { officeUnderAttackScenario } from "./office-under-attack.scenario";

/**
 * Handling company and customer data.
 *
 * Assembled from existing courses, without modifying them:
 * - the reception-terminal hub and conference-TV path from pc-to-darkweb
 * - the office-privacy find-and-sort game from office-under-attack episode 3
 *
 * Not listed on the public library and not seeded. Reachable at
 * ?scenario=data-handling while it is in review.
 */

const OUA_ENV = officeUnderAttackScenario.scene.environment;
const OUA_EVENTS = officeUnderAttackScenario.voiceAttackers.office_under_attack.scoringEvents;
const ART = "/cdn/sim/data-handling/cards";

function deviceConfig<T>(id: string): T {
  const device = officeUnderAttackScenario.devices.find((item) => item.id === id);
  if (!device) throw new Error(`office-under-attack device "${id}" not found`);
  return device.config as T;
}

function beat(id: string): Beat {
  const value = officeUnderAttackScenario.beats.find((item) => item.id === id);
  if (!value) throw new Error(`office-under-attack beat "${id}" not found`);
  return value;
}

function waypoint(id: string): CameraWaypoint {
  const value = officeUnderAttackScenario.scene.waypoints.find((item) => item.id === id);
  if (!value) throw new Error(`office-under-attack waypoint "${id}" not found`);
  return { ...value };
}

function teachReturn(src: Beat): Beat {
  if (!src.teach) return { ...src };
  return { ...src, teach: { ...src.teach, continueLabel: "Return to the briefing" } };
}

function scoring(...ids: string[]): ScoringEvent[] {
  return ids.map((id) => {
    const value = OUA_EVENTS.find((item) => item.id === id);
    if (!value) throw new Error(`office-under-attack scoring event "${id}" not found`);
    return { ...value };
  });
}

const sourceRoom = OUA_ENV.privacyRoom;
if (!sourceRoom) throw new Error("office-under-attack privacy room missing");

const OFFICE_ENV = {
  gltfUrl: OUA_ENV.gltfUrl,
  gltfScale: OUA_ENV.gltfScale,
  gltfPosition: OUA_ENV.gltfPosition,
  gltfRotationDeg: OUA_ENV.gltfRotationDeg,
  gltfColliders: OUA_ENV.gltfColliders,
  procedural: OUA_ENV.procedural,
  enableSplat: false,
  enablePano: false,
  backdropColor: OUA_ENV.backdropColor,
  exposure: OUA_ENV.exposure,
  splat: {},
  receptionTerminal: OUA_ENV.receptionTerminal,
  conferenceTv: OUA_ENV.conferenceTv,
  privacyRoom: {
    items: sourceRoom.items,
    furniture: sourceRoom.furniture?.map((item) => {
      if (item.kind !== "laptop") return item;
      const copy = { ...item };
      delete copy.stickyCaption;
      return copy;
    }),
  },
};

const meetingTv = {
  ...waypoint("wp_meeting_tv_close"),
  position: [-9.0, 1.8, 3.86] as [number, number, number],
  target: [-12.3, 1.8, 3.86] as [number, number, number],
};

interface ChapterTakeaway {
  kicker?: string;
  title: string;
  body: string;
  bullets?: string[];
  narration: string;
}

interface ChapterSpec {
  id: string;
  title: string;
  blurb: string;
  visual: SlideVisualKind;
  slides: { kicker: string; title: string; body: string; bullets: string[]; narration: string; art?: string }[];
  decision: SlideDecision;
  takeaway?: ChapterTakeaway;
}

const CHAPTERS: ChapterSpec[] = [
  {
    "id": "ch1",
    "title": "Recognise what needs protection",
    "blurb": "Recognise personal, higher-risk personal and confidential business information and apply the appropriate handling rules.",
    "visual": "desk_privacy",
    "slides": [
      {
        "kicker": "Introduction",
        "title": "Recognise what needs protection",
        "body": "Every working day, you handle information that matters to other people and your organisation.\nThree overlapping groups need your attention:\nPersonal information: information about identifiable customers, colleagues or applicants—for example, contact details, CVs and performance reviews.\nHigher-risk personal information: details that could cause particular harm if exposed, such as health records, identity documents and financial information.\nConfidential business information: non-public pricing, contracts, business plans, designs and other intellectual property.\nInformation can need protection whether it appears on a screen, on paper or in a conversation.",
        "bullets": [],
        "narration": "Every working day, you handle information that matters to other people and your organisation. Three overlapping groups need your attention: personal information about identifiable people; higher-risk personal information that could cause particular harm if exposed; and confidential business information such as non-public pricing, contracts, plans, designs and intellectual property. Information can need protection whether it appears on a screen, on paper or in a conversation."
      },
      {
        "kicker": "The risk",
        "title": "Ordinary information can create real harm",
        "body": "Sensitive information does not always look sensitive.\nA spreadsheet may have an ordinary filename.\nA printout may have no warning label.\nA conversation may sound like routine office talk.\nAn unlabelled document is not automatically public.",
        "bullets": [],
        "narration": "Sensitive information does not always look sensitive. A spreadsheet may have an ordinary filename, a printout may have no warning label, and a conversation may sound routine. An unlabelled document is not automatically public."
      },
      {
        "kicker": "The risk",
        "title": "What goes wrong when it is missed",
        "body": "If we fail to recognise what needs protection:\nPeople could face fraud, embarrassment or unwanted exposure of private matters.\nCustomers could lose confidence in our organisation.\nCompetitors could gain access to pricing, plans or ideas.",
        "bullets": [],
        "narration": "If we fail to recognise what needs protection, people could face fraud, embarrassment or unwanted exposure, customers could lose confidence, and competitors could gain access to pricing, plans or ideas."
      },
      {
        "kicker": "Illustrative office example",
        "title": "Maya works in the office",
        "body": "Maya works in the office. She comes across everyday situations where private information lurks in unexpected places.\nShe is getting the files ready for a meeting with a visiting supplier. These are documents she will open on screen and send by email — not a physical folder or parcel.",
        "bullets": [],
        "narration": "Maya works in the office, and private information often lurks in ordinary files. She is getting the documents ready for a meeting with a visiting supplier: files she will open on screen and send by email, not a physical folder."
      },
      {
        "kicker": "Illustrative office example",
        "title": "Office example: “It wasn’t marked confidential”",
        "body": "She includes a spreadsheet called “Account overview”:\nCustomer names and contact details: personal information about identifiable people.\nNegotiated discounts: confidential prices the supplier was not meant to see.\nNo confidentiality label: she sends the file because it is in the team’s shared folder.\nThe supplier now has personal and commercial information it was not authorised to receive.\nThe warning signs were in the contents—not the filename, label or folder location.",
        "bullets": [],
        "narration": "She includes a spreadsheet called Account overview. It has customer names, direct contact details and individually negotiated discounts, and there is no confidentiality label. She sends it because the file is in the team’s shared folder. The supplier then has personal and commercial information it was not authorised to receive. The warning signs were in the contents.",
        "art": "ch1-maya"
      },
      {
        "kicker": "What to do",
        "title": "Check the contents and follow the classification",
        "body": "Classification labels tell you how information should be handled.\nPublic — Approved for release to the public.\nInternal — Intended for authorised use within the organisation.\nConfidential — Limited to people with a specific work-related need.\nRestricted — Requires the strongest controls and tightly limited access.",
        "bullets": [],
        "narration": "Classification labels tell you how information should be handled. Public information is approved for release. Internal information is for authorised use inside the organisation. Confidential information is limited to people with a specific work-related need. Restricted information requires the strongest controls.",
        "art": "ch1-classes"
      },
      {
        "kicker": "What to do",
        "title": "Before you share",
        "body": "Your organisation may use different labels and rules. Follow its actual scheme.\nBefore using or sharing information, ask:\nDoes it relate to an identifiable person?\nDoes it reveal something non-public about the business?\nWhat handling rules apply?\nIf the label is missing or unclear, check with the information owner or your manager before sharing it.",
        "bullets": [],
        "narration": "Your organisation may use different labels. Before you share, ask whether the information relates to an identifiable person, whether it reveals something non-public about the business, and which handling rules apply. If the label is missing or unclear, check with the information owner or your manager."
      },
      {
        "kicker": "The situation",
        "title": "Three documents by the printer",
        "body": "You find three documents beside a shared printer. None has a classification label.\nA brochure already approved for public distribution.\nAn applicant’s CV with contact details and interview notes.\nA draft supplier contract containing negotiated prices.",
        "bullets": [],
        "narration": "You find three documents beside a shared printer, and none has a classification label. One is a brochure already approved for public distribution. One is an applicant’s CV with contact details and interview notes. One is a draft supplier contract with negotiated prices."
      }
    ],
    "decision": {
      "prompt": "Your decision: what needs protection?\nWhich documents need to be kept away from visitors, and why?",
      "narration": "You find an approved public brochure, an applicant’s CV with contact details and interview notes, and a draft supplier contract containing negotiated prices beside a shared printer. None has a classification label. Decide which documents need to be kept away from visitors by looking at their contents and whether they have already been approved for public release.",
      "options": [
        {
          "id": "a",
          "label": "Only the CV needs protection because it contains personal information.",
          "feedback": "The CV needs protection, but so does the draft contract. Non-public commercial information can require protection even when it is not personal information."
        },
        {
          "id": "b",
          "label": "The CV and the draft contract need protection; the approved brochure does not.",
          "correct": true,
          "feedback": "The CV and the draft contract. The CV contains personal information. The draft contract contains non-public commercial information. Both need protection even without a label. Secure them and return them through the appropriate internal process. The brochure can be shared because it has already been approved for public distribution."
        },
        {
          "id": "c",
          "label": "All three need protection because none has a classification label.",
          "feedback": "A missing label does not make information public, but the brochure has already been approved for public distribution. Its contents and approval status make it suitable to share."
        }
      ]
    },
    "takeaway": {
      "kicker": "Remember",
      "title": "Recognise before you share",
      "body": "Look at the information itself.\nWho does it concern? What could exposure reveal? Who is authorised to receive it?\nFollow your organisation’s classification and handling rules. If you are unsure, pause and ask.\nNo label does not mean no protection.",
      "bullets": [],
      "narration": "Look at the information itself. Ask who it concerns, what exposure could reveal and who is authorised to receive it. Follow your organisation’s classification and handling rules. If you are unsure, pause and ask. No label does not mean no protection."
    }
  },
  {
    "id": "ch2",
    "title": "Collect and use only what is needed",
    "blurb": "Choose only the information needed for an authorised purpose and seek approval before using it differently.",
    "visual": "desk_privacy",
    "slides": [
      {
        "kicker": "Introduction",
        "title": "Collect for a clear purpose",
        "body": "Forms, surveys, calls and applications all collect information. Before asking for it, you should understand the authorised purpose and what is genuinely needed to complete the task.",
        "bullets": [
          "Use the approved collection procedure.",
          "Give people the relevant privacy information provided by your organisation.",
          "Collect enough for the task, but not extra details for possible future use."
        ],
        "narration": "During an ordinary day, you may collect information through a form, survey, call or application. Start with the authorised purpose: what task is being completed, and which details are necessary for it? Use the approved collection procedure and privacy information. Collect enough to do the work properly, but do not add fields or questions simply because the information might be useful one day."
      },
      {
        "kicker": "The risk",
        "title": "The “just in case” problem",
        "body": "Extra information creates extra exposure without helping the task. People may disclose private details they did not expect the organisation to hold, and inaccurate or outdated details may later influence decisions.",
        "bullets": [
          "Unnecessary data can increase harm if it is lost or misused.",
          "Using information for an unexpected new purpose can undermine trust.",
          "Too little information can also produce wrong or incomplete work."
        ],
        "narration": "The mistake is collecting broadly and deciding what to do with the information later. Every unnecessary detail becomes another item to secure, maintain and eventually dispose of. It can increase harm if exposed and may surprise the person who provided it. Minimisation does not mean collecting too little: it means collecting information that is adequate, relevant and limited to the clear purpose."
      },
      {
        "kicker": "Illustrative office example",
        "title": "Extra fields, extra exposure",
        "body": "A recruiter adds health-history questions to the standard application form for every office vacancy, thinking the answers may be useful later. Applicants provide details unrelated to the role or current adjustment needs.",
        "bullets": [
          "More employees can now see highly private information.",
          "A later hiring discussion is influenced by details that were never needed.",
          "The approved recruitment process offered a separate route for requesting adjustments."
        ],
        "narration": "A recruiter adds health-history questions to every office-job application because the information might be useful later. Applicants disclose private details unrelated to the role. More staff can now see the information, and it begins to influence discussion. The approved process already allowed applicants to request relevant adjustments separately. The problem was not recruitment itself; it was collecting information without a present, authorised need.",
        "art": "ch2-fields"
      },
      {
        "kicker": "What to do",
        "title": "Use the purpose–minimum–approval check",
        "body": "Before collecting or reusing information, apply three checks:",
        "bullets": [
          "Purpose: What authorised task will this information complete?",
          "Minimum: Which specific fields are necessary and sufficient?",
          "Approval: Does the approved form, notice and procedure cover this use? If the purpose changes, pause and obtain the required privacy or business approval first."
        ],
        "narration": "Use a purpose–minimum–approval check. First, name the authorised task. Second, select only the details necessary and sufficient to complete it. Third, use the approved form, procedure and privacy information. If a new project needs existing information for a different purpose, do not improvise. Describe the proposed use and seek the required approval before copying, combining or analysing the information."
      }
    ],
    "decision": {
      "prompt": "You are preparing registration for a supplier briefing. A colleague suggests asking every attendee for a home address and date of birth “in case they are useful later”. The event only needs a name, work contact details and accessibility or dietary information attendees choose to provide. What should you do?",
      "narration": "A colleague wants to add home-address and date-of-birth fields to a supplier briefing registration form, even though the event only needs work contact details and relevant attendee requirements. Decide how to complete registration without collecting information merely because it might become useful later.",
      "options": [
        {
          "id": "a",
          "label": "Add the fields but mark them optional, because optional collection creates no risk.",
          "feedback": "Optional fields still collect information and can create unnecessary exposure or unexpected use. A clear need should come before the question."
        },
        {
          "id": "b",
          "label": "Use the approved registration fields and collect only what the event needs.",
          "correct": true,
          "feedback": "Correct. Use the approved procedure and collect only the information necessary for registration and chosen attendee requirements. A future use should be assessed and approved before any new collection begins."
        },
        {
          "id": "c",
          "label": "Collect everything now, then delete the unnecessary fields after the event.",
          "feedback": "Temporary collection is still collection. It creates avoidable handling and exposure before deletion and does not establish an authorised purpose."
        }
      ]
    },
    "takeaway": {
      "kicker": "Remember",
      "title": "Need it, explain it, limit it",
      "body": "Know the authorised purpose, collect the minimum that completes it, and get approval before using information for something new.",
      "bullets": [],
      "narration": "Collect enough to complete the authorised task, but no more. Use approved procedures and privacy information. If the purpose changes, pause and obtain approval before reusing the information."
    }
  },
  {
    "id": "ch3",
    "title": "Respect access boundaries",
    "blurb": "Use information only for a work-related need, through your own authorised account, and respond safely to unexpected authentication activity.",
    "visual": "desk_privacy",
    "slides": [
      {
        "kicker": "Introduction",
        "title": "Access follows the job",
        "body": "Access to a folder, record or system is permission to perform authorised work—not permission to browse. Your access may change with your duties, projects or role.",
        "bullets": [
          "Open only what you need for the current task.",
          "Use your own account so actions can be attributed correctly.",
          "Treat passwords, codes and authentication approvals as personal security controls."
        ],
        "narration": "You may encounter customer records, colleague files, reports and administrative systems throughout the day. Access is provided for work you are authorised to perform. It is not permission to browse because something is interesting or technically visible. Use your own account, open only what the task requires, and protect passwords, codes and authentication approvals so activity remains controlled and attributable."
      },
      {
        "kicker": "The risk",
        "title": "Curiosity and shortcuts break control",
        "body": "Unauthorised curiosity can expose private or commercially sensitive information. Excessive access also increases the amount of information available if an account is compromised.",
        "bullets": [
          "Shared credentials hide who viewed or changed a record.",
          "A borrowed account may have permissions you were never granted.",
          "Approving an unexpected sign-in request may give someone else access."
        ],
        "narration": "Access controls limit both mistakes and deliberate misuse. Looking up a colleague, celebrity customer or senior manager without a work reason crosses that boundary. Sharing credentials removes accountability and may give the borrower excessive permissions. An unexpected authentication prompt can mean someone else is trying to use your account. Approving it converts a blocked attempt into access to the organisation’s information."
      },
      {
        "kicker": "Illustrative office example",
        "title": "One shared password, no clear record",
        "body": "Jordan covers an absent colleague’s purchasing tasks. The colleague sends Jordan their password instead of arranging temporary access. Jordan updates a supplier record, then another field is changed incorrectly later that day.",
        "bullets": [
          "The audit history shows only the colleague’s account.",
          "No one can quickly establish who made which change.",
          "The shared password may remain known after the cover period ends."
        ],
        "narration": "Jordan needs to cover purchasing work, but no temporary access has been arranged. An absent colleague sends over their password. After several changes, a supplier record is wrong, yet the system shows only one account. The team cannot tell who did what, and the password remains exposed. The work was legitimate; the unauthorised access method created the problem. Proper delegated access would have preserved both progress and accountability.",
        "art": "ch3-access"
      },
      {
        "kicker": "What to do",
        "title": "Use your access, not someone else’s",
        "body": "Follow a simple boundary: need, account, alert.",
        "bullets": [
          "Need: open only information required for your assigned work.",
          "Account: use your individual account and request approved temporary or additional access when duties change.",
          "Alert: deny unexpected authentication requests, change or secure credentials as directed, and report the event through the approved security channel."
        ],
        "narration": "Ask whether the task gives you a genuine work-related need. Use only your own individual account. If cover work or a new project requires more access, request authorised temporary or role-based access rather than borrowing credentials. Never reveal passwords or one-time codes. If an authentication prompt appears when you are not signing in, deny it and report it through the organisation’s security process."
      }
    ],
    "decision": {
      "prompt": "Your work phone asks you to approve a sign-in to an internal system. You are not signing in, but a message immediately follows from someone claiming to be support and asking you to approve it. What should you do?",
      "narration": "You receive an authentication approval request that you did not initiate, followed by a message claiming to be support. Decide how to protect the account without trusting the unexpected request or the contact details supplied with it.",
      "options": [
        {
          "id": "a",
          "label": "Approve it once so support can diagnose the problem.",
          "feedback": "Approving may give another person access. Genuine support should not require you to approve a sign-in you did not initiate."
        },
        {
          "id": "b",
          "label": "Deny the request and report it through a known security or support channel.",
          "correct": true,
          "feedback": "Correct. Deny an authentication request you did not initiate. Use a known internal contact route to report it and follow instructions for securing the account."
        },
        {
          "id": "c",
          "label": "Ignore the request and delete the message without telling anyone.",
          "feedback": "Denying and reporting is safer. Reporting allows the organisation to investigate a possible credential compromise and protect related systems."
        }
      ]
    },
    "takeaway": {
      "kicker": "Remember",
      "title": "Need to know, own account",
      "body": "Access only what your work requires, use your own account, and deny and report authentication activity you did not initiate.",
      "bullets": [],
      "narration": "A visible record is not automatically yours to open. Use information for an authorised work need, use your own account, and deny and report unexpected authentication requests."
    }
  },
  {
    "id": "ch4",
    "title": "Store information in approved places",
    "blurb": "Keep work information in approved applications, accounts, devices and storage so it remains protected, recoverable and available to authorised colleagues.",
    "visual": "desk_privacy",
    "slides": [
      {
        "kicker": "Introduction",
        "title": "Approved places keep work manageable",
        "body": "You create and receive records in email, collaboration tools, business applications and documents. Store them where your organisation has approved them to be kept.",
        "bullets": [
          "Use approved work accounts and managed devices.",
          "Save the authoritative copy in the designated system or shared location.",
          "Use an approved transfer or offline method when normal access is unavailable."
        ],
        "narration": "Work information moves through applications, email, devices and shared folders every day. Approved storage is not only about secrecy. It helps the organisation apply access controls, backups, retention, monitoring and recovery, while allowing authorised colleagues to find the current record. Use approved work accounts and devices, and make sure the authoritative copy reaches the designated system or shared location."
      },
      {
        "kicker": "The risk",
        "title": "Convenient copies become blind spots",
        "body": "Personal email, consumer cloud storage, unapproved messaging apps and unmanaged removable media can place information outside organisational controls.",
        "bullets": [
          "The copy may be exposed, lost or retained without anyone knowing.",
          "Colleagues may be unable to find the current record.",
          "Security, backup, access removal and deletion processes may not reach it."
        ],
        "narration": "A quick copy to personal email or cloud storage can outlive the task and escape organisational safeguards. An unapproved messaging app may keep attachments in backups. An unmanaged USB device can be lost or infected. These copies also fragment the record: colleagues may work from an older version, and the organisation may be unable to recover, retain or delete the information correctly."
      },
      {
        "kicker": "Illustrative office example",
        "title": "Sent home to finish later",
        "body": "Rina forwards a customer complaint file to her personal email so she can finish it at home. She edits the attachment on a personal tablet but forgets to return the final version to the case system.",
        "bullets": [
          "Customer details remain in a personal mailbox and device backup.",
          "The case team acts on the older version.",
          "Rina’s completed response is unavailable when she is absent."
        ],
        "narration": "Rina wants to finish a customer complaint at home, so she forwards the file to personal email and edits it on a personal tablet. The final version never reaches the case system. Customer details remain in an unmanaged mailbox and backup, while colleagues act on an older record. An approved remote-access or checked-out workflow would have allowed the work to continue without creating an uncontrolled copy.",
        "art": "ch4-store"
      },
      {
        "kicker": "What to do",
        "title": "Use an approved route end to end",
        "body": "Keep the whole task inside approved controls:",
        "bullets": [
          "Open and edit through approved work applications, accounts and devices.",
          "Save the current record where authorised colleagues can find it.",
          "If a file is too large, access fails or offline work is necessary, use the approved alternative or contact support for a sanctioned transfer method.",
          "Remove temporary approved copies when the procedure requires it."
        ],
        "narration": "Use the approved route from start to finish. Work through managed accounts, applications and devices, and return the current record to its designated location. If the ordinary route fails, do not invent a personal workaround. Use an approved secure-transfer, offline or remote-access option, or ask support for a sanctioned method. Follow the procedure for removing any temporary copies after successful transfer."
      }
    ],
    "decision": {
      "prompt": "A colleague cannot upload a project file to the approved shared folder and asks you to send it through a personal messaging app so work can continue. What should you do?",
      "narration": "An approved shared folder is temporarily unavailable, and a colleague suggests using a personal messaging app. Decide how to keep the work moving without creating an uncontrolled copy outside the organisation’s systems.",
      "options": [
        {
          "id": "a",
          "label": "Send it through the app, then ask the colleague to delete it later.",
          "feedback": "Deletion cannot restore organisational control over backups, synced devices or other copies. Use an approved alternative from the start."
        },
        {
          "id": "b",
          "label": "Use an approved fallback or ask support for a sanctioned transfer method.",
          "correct": true,
          "feedback": "Correct. An approved secure-transfer or offline route can complete the work while preserving access, security and record-keeping controls."
        },
        {
          "id": "c",
          "label": "Wait indefinitely and do not tell anyone that the approved folder is unavailable.",
          "feedback": "The task can often continue safely through an approved fallback. Reporting the access problem also allows support to restore the normal route."
        }
      ]
    },
    "takeaway": {
      "kicker": "Remember",
      "title": "Keep work in approved places",
      "body": "Use approved accounts, applications, devices and storage. When the normal route fails, choose an approved fallback—not a personal workaround.",
      "bullets": [],
      "narration": "Approved storage keeps information protected, recoverable and available. Keep the authoritative record there, and use a sanctioned alternative when the normal route is unavailable."
    }
  },
  {
    "id": "ch5",
    "title": "Share information safely",
    "blurb": "Check the recipient, minimise the content and apply appropriate permissions and redaction before sharing information.",
    "visual": "desk_privacy",
    "slides": [
      {
        "kicker": "Introduction",
        "title": "Sharing is a controlled action",
        "body": "You share information through email, attachments, collaboration links, reports and spreadsheets. Before sending, check three things: recipient, content and access.",
        "bullets": [
          "Address it to the intended authorised people.",
          "Include only what they need for the task.",
          "Choose view, edit, download and expiry settings that match their role."
        ],
        "narration": "Sharing may mean attaching a document, granting access to a folder, sending a link or publishing a report. Each action creates a new audience. Before you send, confirm the intended and authorised recipients, reduce the content to what they need, and set permissions that match what they must do. A legitimate recipient does not automatically need every field or editing rights."
      },
      {
        "kicker": "The risk",
        "title": "A correct file can still overshare",
        "body": "A document’s visible page may be appropriate while hidden content is not. Spreadsheets can contain hidden rows, sheets or formulas; documents can retain comments, tracked changes and metadata.",
        "bullets": [
          "Autocomplete can select the wrong recipient.",
          "An open link can travel beyond the intended group.",
          "Black boxes or white text may hide content visually without removing it."
        ],
        "narration": "Safe sharing requires more than checking the filename. A spreadsheet may carry hidden tabs or filtered rows. A document may include comments, tracked changes or earlier text. A collaboration link may allow anyone to edit or forward access. Visual covering is not reliable redaction because the underlying content may remain. These details can expose personal, commercial or internal information unintentionally."
      },
      {
        "kicker": "Illustrative office example",
        "title": "The hidden sheet travelled too",
        "body": "Noah sends a pricing workbook to a supplier. The visible sheet contains the agreed quotation, but a hidden sheet lists other customers, discounts and internal margin calculations. The link also grants edit access.",
        "bullets": [
          "The supplier can unhide information it was not authorised to receive.",
          "Editing could alter the team’s source workbook.",
          "The intended task required only a clean, view-only quotation."
        ],
        "narration": "Noah sends a supplier a workbook whose visible sheet contains the correct quotation. He does not notice a hidden sheet containing customer names, discounts and internal margins, and the sharing link allows editing. The supplier can see information it does not need and could alter the source. Exporting a clean quotation and granting named-recipient view access would have completed the same task safely.",
        "art": "ch5-share"
      },
      {
        "kicker": "What to do",
        "title": "Check recipient, content and access",
        "body": "Pause before the final send or share action:",
        "bullets": [
          "Recipient: verify addresses, groups and external domains.",
          "Content: remove unrelated fields and inspect hidden sheets, rows, comments, tracked changes and metadata.",
          "Access: use named recipients and the least permission needed—usually view rather than edit.",
          "Redaction: use an approved method, then reopen the final copy to confirm removed content cannot be recovered."
        ],
        "narration": "Use a final recipient–content–access check. Confirm every address and external domain. Open the exact attachment or link target, remove unrelated information, and inspect hidden sheets, comments, tracked changes and metadata. Give named recipients only the permission they need. If redaction is required, use an approved tool or process and independently reopen the finished copy to confirm the underlying information is gone."
      }
    ],
    "decision": {
      "prompt": "You must send an external adviser three pages from a report. The current link gives anyone with the link edit access to the full document, which contains comments and an appendix of customer details. What should you do?",
      "narration": "An external adviser needs three pages, but the current link exposes the full editable document, including comments and customer details. Decide how to deliver the necessary content without granting broader access.",
      "options": [
        {
          "id": "a",
          "label": "Send the link and tell the adviser to read only the three relevant pages.",
          "feedback": "Instructions do not limit technical access. The adviser would still receive unnecessary content and editing rights."
        },
        {
          "id": "b",
          "label": "Create a checked clean copy of the three pages and give the named adviser view-only access.",
          "correct": true,
          "feedback": "Correct. This limits both content and permissions. Inspect the clean copy for comments, metadata and hidden material before sharing it with the verified recipient."
        },
        {
          "id": "c",
          "label": "Cover the appendix with black rectangles and email the full editable file.",
          "feedback": "Visual covering may leave underlying text recoverable, and the full editable file still contains unnecessary material."
        }
      ]
    },
    "takeaway": {
      "kicker": "Remember",
      "title": "Share the right minimum",
      "body": "Right recipient, necessary content, least access. Check the final file or link—not just the version you meant to send.",
      "bullets": [],
      "narration": "Before sharing, verify the recipient, minimise the content and choose the least access needed. Inspect the actual final attachment or link and use proper redaction where required."
    }
  },
  {
    "id": "ch6",
    "title": "Verify requests and external recipients",
    "blurb": "Verify both identity and authority through an independent channel before disclosing information or using a new external destination.",
    "visual": "desk_privacy",
    "slides": [
      {
        "kicker": "Introduction",
        "title": "Verify both person and permission",
        "body": "Requests for information arrive by email, phone, message and collaboration tools. A familiar name or correct job title does not prove the request is genuine or authorised.",
        "bullets": [
          "Identity: Is this really the person or organisation claimed?",
          "Authority: Are they entitled to receive this specific information?",
          "Destination: Is the supplier, portal or external location approved for this use?"
        ],
        "narration": "You may receive routine-looking requests from managers, customers, suppliers or public bodies. Verification has three parts. Confirm the requester’s identity, confirm their authority to receive the specific information, and confirm that the proposed external destination is approved. A genuine employee may still lack permission, and an approved supplier may not be approved for every dataset or purpose."
      },
      {
        "kicker": "The risk",
        "title": "Urgency can hide a wrong request",
        "body": "Impersonators use familiar names, copied signatures and urgent language to push people past normal checks. Genuine requests can also be misdirected or exceed the requester’s authority.",
        "bullets": [
          "Disclosure may expose customers, applicants or business plans.",
          "A new portal may create an unapproved copy or data location.",
          "Responding through contact details in the request may reach the impersonator again."
        ],
        "narration": "The mistake is treating plausibility as proof. An urgent request may appear to come from a director, supplier or colleague, yet use a lookalike address or compromised account. Even when identity is genuine, the person may be asking for more than they are authorised to receive. Verification through an independent, known channel prevents urgency from becoming an uncontrolled disclosure."
      },
      {
        "kicker": "Illustrative office example",
        "title": "A convincing request from a new address",
        "body": "Elena receives an urgent email carrying her director’s name and signature. It asks for applicant CVs to be sent to a new consultant before an afternoon meeting. Elena replies with the files.",
        "bullets": [
          "The address differs by one character from the director’s real domain.",
          "No consultant has been approved for recruitment data.",
          "The applicants’ contact details and interview history reach an unauthorised recipient."
        ],
        "narration": "Elena receives an urgent request bearing her director’s name and familiar signature. It asks for applicant CVs to be sent to a new consultant. She replies without checking. The sender’s domain differs by one character, and no consultant was approved to receive recruitment data. A quick call using the director’s known directory number and a supplier-approval check would have exposed both problems.",
        "art": "ch6-verify"
      },
      {
        "kicker": "What to do",
        "title": "Pause, verify independently, then share",
        "body": "When a request is unusual, urgent or external:",
        "bullets": [
          "Do not use the request’s links, reply address or phone number for verification.",
          "Contact the person or organisation through a known directory, established conversation or verified website.",
          "Confirm the requester’s authority and the exact minimum information required.",
          "Obtain required approval before sharing with a new supplier or introducing a new storage or processing location."
        ],
        "narration": "Pause without abandoning legitimate work. Verify identity using a known number, directory entry, established conversation or independently located official contact. Then confirm authority: what information is requested, for what purpose, and whether the recipient is approved. If a new supplier, portal or data location is involved, obtain the required approval first. Once verified, share only the necessary information through the approved route."
      }
    ],
    "decision": {
      "prompt": "A known supplier contact emails from their normal address and asks you to upload customer records to a new portal today. The portal has not appeared in previous instructions. What should you do?",
      "narration": "A familiar supplier contact requests an urgent upload to a new portal. Their identity may be genuine, but the new destination and authority for this dataset have not been established. Decide how to verify both before sharing.",
      "options": [
        {
          "id": "a",
          "label": "Upload the records because the email address and supplier are familiar.",
          "feedback": "A familiar identity does not establish authority for a new dataset or approval of a new data location."
        },
        {
          "id": "b",
          "label": "Verify through a known contact route and confirm the portal and sharing are approved.",
          "correct": true,
          "feedback": "Correct. Independently confirm the request, the supplier’s authority, the minimum data required and approval of the new portal before uploading anything."
        },
        {
          "id": "c",
          "label": "Refuse all future requests from the supplier.",
          "feedback": "The request may be legitimate. Independent verification and the appropriate approval route allow authorised work to proceed safely."
        }
      ]
    },
    "takeaway": {
      "kicker": "Remember",
      "title": "Verify before you move data",
      "body": "Confirm who is asking, what they are authorised to receive and whether the destination is approved—using a channel you already trust.",
      "bullets": [],
      "narration": "A familiar name proves neither identity nor authority. Verify independently, confirm the minimum necessary information and use only an approved external destination."
    }
  },
  {
    "id": "ch7",
    "title": "Handle data safely in AI and online tools",
    "blurb": "Treat uploads to AI and online services as disclosures, use only approved tools and information, and verify outputs before relying on them.",
    "visual": "desk_privacy",
    "slides": [
      {
        "kicker": "Introduction",
        "title": "Online tools receive what you upload",
        "body": "AI assistants, translation sites, file converters and meeting assistants can make work faster. When you paste text, upload a file or enable a bot, information is sent to a service provider.",
        "bullets": [
          "Approval must cover the specific tool, information and purpose.",
          "Removing names may not remove every way a person can be identified.",
          "Generated or converted output still needs checking."
        ],
        "narration": "AI assistants and online services can support drafting, translation, conversion and meeting work. But pasting text, uploading a file or inviting a meeting bot transfers information to another service. Before using one, confirm that the organisation has approved that tool for the particular information and purpose. Removing names is not always enough, and any output must be checked before it affects people or work."
      },
      {
        "kicker": "The risk",
        "title": "Useful tools can create two risks",
        "body": "First, the upload may disclose personal or confidential business information to a provider or create a new stored copy. Second, the result may be incomplete, altered or confidently wrong.",
        "bullets": [
          "Contracts, prompts, recordings and files may contain hidden context.",
          "Several indirect details can identify someone even without a name.",
          "Incorrect output can misstate facts, obligations or decisions."
        ],
        "narration": "There are two separate decisions: whether the information may be submitted, and whether the result is reliable. A public tool may retain queries or make them visible to its provider. Removing a name does not anonymise a record when dates, locations or unique events still identify someone. AI can also produce convincing errors, so approval to use a tool is not approval to trust every answer."
      },
      {
        "kicker": "Illustrative office example",
        "title": "A transcript became a provider upload",
        "body": "Sam removes customer names from a support-call transcript and pastes it into a public AI assistant for a summary. The transcript still contains an order number, rare product fault and delivery location.",
        "bullets": [
          "The customer may still be identifiable from the combined details.",
          "The provider receives confidential support information.",
          "The summary invents a promised refund that was never discussed."
        ],
        "narration": "Sam removes customer names from a support-call transcript and uploads it to a public AI assistant. Order details, a rare fault and a delivery location still make the customer identifiable. The provider receives the transcript, and the generated summary adds a refund promise that was never made. The shortcut creates both a disclosure risk and an accuracy risk. Removing names and checking grammar were not enough.",
        "art": "ch7-ai"
      },
      {
        "kicker": "What to do",
        "title": "Approve, minimise, check",
        "body": "Use a three-step rule for AI and online services:",
        "bullets": [
          "Approve: confirm the tool is authorised for this type of information and task.",
          "Minimise: submit only the content needed; use approved anonymisation or a safe substitute where appropriate.",
          "Check: compare the result with the source, verify important facts and calculations, and keep human responsibility for decisions.",
          "If the task cannot be completed safely, use an approved internal service or another authorised workflow."
        ],
        "narration": "First, confirm that the specific tool is approved for the information and purpose—not merely that someone else has used it. Second, minimise the input and use approved anonymisation or synthetic content where suitable. Third, verify the output against reliable sources before using it. If approval is absent, use an approved internal service, request an authorised option or complete the task through another safe workflow."
      }
    ],
    "decision": {
      "prompt": "You need to translate an employee’s medical-adjustment letter. A free translation website is quick, but it is not on the approved-tools list. What should you do?",
      "narration": "A medical-adjustment letter needs translation, but the convenient free website is not approved for that sensitive information. Decide how to complete the legitimate task without creating an unauthorised disclosure.",
      "options": [
        {
          "id": "a",
          "label": "Remove the employee’s name and upload the whole letter to the free website.",
          "feedback": "Other details may still identify the employee, and removing a name does not make an unapproved service suitable."
        },
        {
          "id": "b",
          "label": "Use an approved translation route authorised for the letter and verify the result.",
          "correct": true,
          "feedback": "Correct. Use a service approved for that information and purpose, submit only what is necessary, and have the translation checked before relying on it."
        },
        {
          "id": "c",
          "label": "Refuse to translate the letter under any circumstances.",
          "feedback": "The work can be completed through an approved translation service or authorised internal process. The safe answer is not automatically to abandon the task."
        }
      ]
    },
    "takeaway": {
      "kicker": "Remember",
      "title": "Treat every upload as sharing",
      "body": "Use only tools approved for the information and purpose, minimise the input, and verify the output before relying on it.",
      "bullets": [],
      "narration": "An AI prompt, conversion upload or meeting-bot invitation is a disclosure. Confirm approval, minimise what you provide and check the result against a reliable source."
    }
  },
  {
    "id": "ch8",
    "title": "Protect information around you",
    "blurb": "Control your physical and virtual surroundings so screens, papers, conversations, devices and meetings are not exposed to unintended people.",
    "visual": "desk_privacy",
    "slides": [
      {
        "kicker": "Introduction",
        "title": "Protection follows the information",
        "body": "Information remains sensitive outside its usual system. People can see a screen, collect a printout, overhear a call, photograph a whiteboard or join a meeting unexpectedly.",
        "bullets": [
          "Lock screens and secure devices when unattended.",
          "Collect printing promptly and store papers safely.",
          "Check who can see or hear before discussing, displaying, photographing or recording work."
        ],
        "narration": "Information can be exposed without a system being hacked. A visitor may see a screen, someone may collect a forgotten printout, or people nearby may overhear a call. Remote work, travel and online meetings change the surroundings but not the need for protection. Build a brief environment check into your routine before viewing, discussing, printing, photographing, sharing or recording work."
      },
      {
        "kicker": "The risk",
        "title": "Nearby people and devices can expose it",
        "body": "A small physical lapse can reveal private or commercial information and may leave no clear record of who received it.",
        "bullets": [
          "Unlocked devices can be viewed or changed.",
          "Public conversations and visible screens can expose details to strangers.",
          "Screen sharing, notifications, photographs and recordings can capture more than intended.",
          "Uncollected papers can be read, removed or copied."
        ],
        "narration": "The risk is often accidental and immediate. A notification can reveal a customer name during screen sharing. A café conversation can expose a complaint or business plan. A visitor can photograph a whiteboard, and a meeting recording can preserve chat, voices and shared files. These exposures can affect individuals, customers and the organisation even when no one intended to disclose anything."
      },
      {
        "kicker": "Illustrative office example",
        "title": "The café call",
        "body": "A project lead joins a video call from a busy café. The discussion includes a customer dispute and an unreleased product date. The lead uses headphones but speaks loudly and leaves the laptop unlocked while collecting an order.",
        "bullets": [
          "Nearby customers hear the conversation.",
          "The screen remains visible to passers-by.",
          "Headphones protect incoming sound, not what the employee says or displays."
        ],
        "narration": "A project lead takes a call in a café about a customer dispute and an unreleased product. Headphones prevent other people hearing the remote participants, but not the lead’s own voice. When the lead leaves the unlocked laptop to collect an order, the screen is visible too. Moving to a private setting, limiting the discussion or postponing sensitive parts would have kept the work safe.",
        "art": "ch8-around"
      },
      {
        "kicker": "What to do",
        "title": "Control the space before you work",
        "body": "Scan your physical and virtual surroundings:",
        "bullets": [
          "See: position screens carefully, share a single window where possible and close unrelated files and notifications.",
          "Hear: move sensitive conversations to a private place and use appropriate volume.",
          "Secure: lock devices, collect papers and keep visitors away from unattended information.",
          "Meet: confirm participants and approval before recording, photographing, transcribing or adding an assistant."
        ],
        "narration": "Before handling sensitive work, check who can see and hear. Position the screen, close unrelated windows and notifications, and share only the intended application. Move sensitive calls to a private place. Lock devices whenever you step away, collect printing immediately and secure papers. In meetings, confirm participants and follow the approved process before recording, photographing, transcribing or enabling an automated assistant."
      }
    ],
    "decision": {
      "prompt": "You are about to present one document in an online meeting with external guests. Your desktop also shows customer files and message previews. What should you do?",
      "narration": "External guests need to see one document, while your desktop contains customer files and message previews. Decide how to present the required material without exposing the rest of your workspace.",
      "options": [
        {
          "id": "a",
          "label": "Share the whole desktop and ask guests to ignore anything unrelated.",
          "feedback": "Guests can still see notifications, filenames or other windows. Instructions do not prevent exposure."
        },
        {
          "id": "b",
          "label": "Close unrelated content, disable previews and share only the required window.",
          "correct": true,
          "feedback": "Correct. Confirm the participants, prepare the screen and share only the intended window. Also check whether the meeting is being recorded or transcribed."
        },
        {
          "id": "c",
          "label": "Email screenshots of the whole desktop before the meeting.",
          "feedback": "Screenshots would create a lasting copy of the same unnecessary information and increase the exposure."
        }
      ]
    },
    "takeaway": {
      "kicker": "Remember",
      "title": "Look, listen, lock",
      "body": "Check who can see or hear, reveal only what the task needs, and secure screens, papers and devices whenever your attention moves away.",
      "bullets": [],
      "narration": "Protect information in every setting. Look around, listen for who is nearby, share only what is needed and lock or secure material when you step away."
    }
  },
  {
    "id": "ch9",
    "title": "Keep information accurate and usable",
    "blurb": "Use authoritative records, verify important changes and protect information against unauthorised alteration, duplication and loss of availability.",
    "visual": "desk_privacy",
    "slides": [
      {
        "kicker": "Introduction",
        "title": "Reliable work needs a trusted record",
        "body": "Information must be correct enough for its purpose, available when authorised people need it, and protected from unauthorised changes. Use the designated authoritative record rather than a convenient personal copy.",
        "bullets": [
          "Check the source and version before acting.",
          "Verify important changes through the approved process.",
          "Record corrections where authorised colleagues will see them."
        ],
        "narration": "Accurate and usable information supports decisions, payments, deliveries and customer service. Begin with the designated authoritative system or record. Check its source, date and version before relying on it, especially when the consequences of an error are significant. Make verified changes through the approved process so colleagues see the correction and the organisation can recover or explain the record later."
      },
      {
        "kicker": "The risk",
        "title": "Stale or altered data causes real mistakes",
        "body": "Uncontrolled duplicates can disagree with the authoritative record. An unverified change can redirect a payment, send information to the wrong address or affect a decision about a person.",
        "bullets": [
          "Old copies can keep circulating after a correction.",
          "Overwriting evidence can hide what changed and why.",
          "Accidental deletion or unavailable records can stop essential work."
        ],
        "narration": "The harm is not limited to incorrect spelling. Stale contact details may expose correspondence to another person. Wrong financial details can misdirect funds. An altered applicant or employee record can affect a decision. Uncontrolled duplicates make corrections unreliable, while deletion or loss of availability can interrupt service. Accuracy, integrity and availability must be protected together."
      },
      {
        "kicker": "Illustrative office example",
        "title": "The offline copy became the wrong answer",
        "body": "Tariq downloads the customer delivery list and keeps updating it on his laptop. Another team corrects an address in the customer system, but Tariq’s copy does not change. He later sends his list to dispatch.",
        "bullets": [
          "A parcel goes to the former address.",
          "The customer’s correction appears to have been ignored.",
          "Two competing versions make the error harder to trace and fix."
        ],
        "narration": "Tariq keeps an offline customer delivery list because it is convenient. Another team corrects a customer’s address in the authoritative system, but his private copy remains unchanged. Dispatch uses Tariq’s list and sends a parcel to the former address. The customer’s privacy and service are affected, and competing versions make the cause harder to trace. The working copy should have been refreshed or avoided.",
        "art": "ch9-record"
      },
      {
        "kicker": "What to do",
        "title": "Verify, update and preserve the source",
        "body": "Use a source–change–recovery check:",
        "bullets": [
          "Source: work from the designated authoritative record and confirm the current version.",
          "Change: verify significant updates, record the source and use the approved correction process.",
          "Recovery: avoid uncontrolled duplicates, preserve history where required and report missing, corrupted or unavailable records promptly.",
          "If accuracy is disputed, flag and route the issue rather than silently choosing a version."
        ],
        "narration": "Start from the authoritative record. Before a significant change, verify the request and supporting source. Update through the approved correction process so the right people and connected systems receive it. Avoid private master copies; use controlled working copies only when the process allows them. Preserve history where required, and report disputed, deleted, corrupted or unavailable information so the responsible team can correct or recover it."
      }
    ],
    "decision": {
      "prompt": "An email asks you to replace a supplier’s verified bank details in the finance system. The message looks genuine, but the change has not passed the organisation’s verification process. What should you do?",
      "narration": "A message requests a significant change to an authoritative supplier record, but the approved verification has not occurred. Decide how to protect the record’s accuracy while allowing a legitimate change to proceed.",
      "options": [
        {
          "id": "a",
          "label": "Update the system immediately, then verify the change after payment.",
          "feedback": "The unverified change could affect a payment before anyone checks it. Verification must happen first."
        },
        {
          "id": "b",
          "label": "Verify through the approved process, then update the authoritative record with the source recorded.",
          "correct": true,
          "feedback": "Correct. Significant changes should be independently verified and made in the authoritative system with an appropriate record of the source."
        },
        {
          "id": "c",
          "label": "Keep the new details only in a personal spreadsheet and use them manually.",
          "feedback": "A personal duplicate bypasses verification and creates a competing record that colleagues may not see or correct."
        }
      ]
    },
    "takeaway": {
      "kicker": "Remember",
      "title": "Use and protect the trusted record",
      "body": "Check the source and version, verify important changes, and keep corrections in the authoritative system.",
      "bullets": [],
      "narration": "Reliable work starts with the authoritative record. Verify significant changes, avoid uncontrolled duplicates and report accuracy or availability problems promptly."
    }
  },
  {
    "id": "ch10",
    "title": "Retain, dispose of and hand over information correctly",
    "blurb": "Follow authorised retention, preservation, disposal and handover instructions throughout projects, role changes and departures.",
    "visual": "desk_privacy",
    "slides": [
      {
        "kicker": "Introduction",
        "title": "Information has an end of life",
        "body": "Records should remain available for as long as the organisation needs them, then be securely disposed of when authorised. Different records may have different retention requirements.",
        "bullets": [
          "Use the organisation’s retention schedule and approved storage.",
          "Do not keep extra copies “just in case”.",
          "Preserve information when an authorised hold or investigation instruction applies.",
          "Hand over records and return devices when responsibilities change."
        ],
        "narration": "Information management continues after today’s task ends. Records may need to remain available for operational, contractual, legal or historical reasons, but not forever. Follow the organisation’s retention schedule and any authorised preservation instruction. Avoid personal archives and unnecessary duplicates. When a project, role or employment ends, hand over authoritative records, return devices and allow access to be changed through the formal process."
      },
      {
        "kicker": "The risk",
        "title": "Keeping everything is not safer",
        "body": "Old copies can become inaccurate, hard to find and unnecessarily exposed. Premature deletion can remove evidence or information needed by customers and colleagues.",
        "bullets": [
          "Ordinary deletion may not securely remove data from media.",
          "Unreturned devices and forgotten folders can retain accessible copies.",
          "Deleting during an authorised preservation hold can obstruct an investigation or obligation.",
          "Poor handover can make essential work unavailable."
        ],
        "narration": "Two opposite mistakes cause harm: keeping everything indefinitely and deleting without authority. Unnecessary archives enlarge the amount exposed in an incident and make requests or corrections harder. Insecure disposal may leave recoverable data on paper or devices. Premature deletion can remove required records, while poor handover can leave a team unable to continue work. The approved schedule and preservation instructions resolve that balance."
      },
      {
        "kicker": "Illustrative office example",
        "title": "The project ended; the copies remained",
        "body": "After a research project closes, Aisha leaves exports on her laptop and in a private project folder in case questions arise. She later changes roles and returns the laptop without completing the records handover.",
        "bullets": [
          "The official project file is missing final notes.",
          "Unnecessary participant details remain in two forgotten locations.",
          "The new project owner cannot answer a customer query from the approved record."
        ],
        "narration": "Aisha finishes a research project but keeps exports on her laptop and in a private folder, just in case. She changes roles without transferring the final notes or reviewing the copies. The official file is incomplete, the new owner cannot answer a query, and participant details remain in forgotten locations. A planned handover and retention review would have kept what was required and removed what was not.",
        "art": "ch10-retain"
      },
      {
        "kicker": "What to do",
        "title": "Retain, hand over or dispose as directed",
        "body": "At the end of a task, project or role:",
        "bullets": [
          "Identify the authoritative records and move them to the approved owner or location.",
          "Apply the retention schedule; do not invent a period.",
          "Obey authorised preservation or legal-hold instructions, even if routine deletion is due.",
          "Use approved confidential-waste and media-sanitisation routes—ordinary deletion may be insufficient.",
          "Return devices, keys and media and confirm access changes."
        ],
        "narration": "Identify the authoritative records and ensure the responsible person or system receives them. Follow the retention schedule rather than choosing your own period. If an authorised preservation hold applies, stop routine deletion and follow its instructions. Dispose of paper through confidential waste and electronic media through the approved sanitisation process. During role changes or departure, return equipment and complete the handover and access-removal steps."
      }
    ],
    "decision": {
      "prompt": "A routine deletion date arrives for project records, but the records team has issued an instruction to preserve material connected with an investigation. What should you do?",
      "narration": "The usual retention schedule says records are due for deletion, but an authorised preservation instruction now applies. Decide which instruction controls and how to avoid creating unnecessary extra copies.",
      "options": [
        {
          "id": "a",
          "label": "Delete the records because the original schedule came first.",
          "feedback": "An authorised preservation instruction changes the immediate action. Routine deletion should stop for the covered records."
        },
        {
          "id": "b",
          "label": "Preserve the covered records in the approved location and follow the hold instructions.",
          "correct": true,
          "feedback": "Correct. Follow the authorised preservation instruction, keep the records controlled and do not create extra personal copies."
        },
        {
          "id": "c",
          "label": "Copy everything to personal storage before deleting the official records.",
          "feedback": "Personal storage creates an uncontrolled copy and may undermine both preservation and security requirements."
        }
      ]
    },
    "takeaway": {
      "kicker": "Remember",
      "title": "Keep what is required, no more",
      "body": "Follow the retention schedule, obey preservation instructions, hand over authoritative records and use approved secure disposal.",
      "bullets": [],
      "narration": "Do not keep information forever or delete it on your own timetable. Follow retention and preservation instructions, complete handovers and dispose through approved secure routes."
    }
  },
  {
    "id": "ch11",
    "title": "Recognise privacy requests",
    "blurb": "Recognise informal requests about personal information and route them promptly without making unauthorised promises or disclosures.",
    "visual": "desk_privacy",
    "slides": [
      {
        "kicker": "Introduction",
        "title": "Everyday words can be a privacy request",
        "body": "A customer, colleague or applicant may ask to see information held about them, correct it, delete it or complain about its use. They may ask by email, phone, message, social media or conversation.",
        "bullets": [
          "They do not need to use legal terms or a special form.",
          "Record what they asked and when it was received.",
          "Route it promptly through the organisation’s privacy-request process."
        ],
        "narration": "Privacy requests often sound informal: “What information do you have about me?”, “That address is wrong,” or “Please delete my application.” The person may contact any employee and may not mention a law or use a special form. Your job is to recognise the request, capture it accurately and route it promptly. The privacy or legal team handles identity checks, scope, deadlines and exceptions."
      },
      {
        "kicker": "The risk",
        "title": "Good intentions can mishandle a request",
        "body": "Responding from your own files may disclose information to the wrong person, omit records held elsewhere or reveal information about other people. Promising deletion may also be wrong because some records must be retained.",
        "bullets": [
          "Delay can reduce the specialist team’s response time.",
          "Unnecessary identity documents can create more sensitive data.",
          "An ordinary employee should not decide exemptions, deadlines or the final response."
        ],
        "narration": "Trying to solve a privacy request alone can create a second problem. You may send incomplete information, disclose another person’s details or delete a record that must be preserved. Asking for excessive identification creates more information to protect. Delayed routing also leaves less time for the responsible team. Acknowledge the request, but do not promise an outcome or disclose records outside the approved process."
      },
      {
        "kicker": "Illustrative office example",
        "title": "A quick reply created a bigger problem",
        "body": "A customer tells a sales representative, “Send me everything you hold about me and correct my old address.” The representative emails a CRM screenshot immediately and promises that all records are now corrected.",
        "bullets": [
          "The screenshot includes notes about another customer.",
          "Other systems still contain the old address.",
          "The privacy team does not learn about the access-and-correction request until the customer complains."
        ],
        "narration": "A customer asks a sales representative to send everything held about them and correct an old address. Wanting to help, the representative emails a CRM screenshot and promises the correction is complete. The screenshot includes another customer’s notes, and other systems still hold the old address. Promptly recording and routing the request would have enabled proper identity checking, a complete search and a coordinated correction.",
        "art": "ch11-request"
      },
      {
        "kicker": "What to do",
        "title": "Recognise, record and route",
        "body": "When someone asks about their personal information:",
        "bullets": [
          "Recognise access, correction, deletion and complaint language, even when informal.",
          "Record the requester’s words, contact details, time received and relevant context without adding unnecessary information.",
          "Acknowledge receipt without promising deletion, disclosure or a deadline you are not authorised to give.",
          "Route it immediately to the designated team and follow its identity-verification instructions."
        ],
        "narration": "Listen for requests to access, correct or delete personal information and complaints about how it was used. Record the person’s own words, when the request arrived and how they can be contacted. Acknowledge that it will be routed, but do not promise a result. Send it promptly to the designated privacy process. Do not disclose records or collect identity documents unless that process instructs you to."
      }
    ],
    "decision": {
      "prompt": "An unsuccessful applicant emails you: “Delete my application and tell me who has seen it.” You do not normally handle privacy requests. What should you do?",
      "narration": "An applicant informally asks for deletion and information about access to their application. You do not decide privacy-request outcomes, but you may be the first person to receive a valid request. Choose the correct immediate action.",
      "options": [
        {
          "id": "a",
          "label": "Delete the application yourself and promise that every copy is gone.",
          "feedback": "You may miss other systems or delete a record that must be retained. Only the authorised process should determine the outcome."
        },
        {
          "id": "b",
          "label": "Record and route the request promptly, acknowledge it and avoid promising an outcome.",
          "correct": true,
          "feedback": "Correct. Preserve the requester’s wording and receipt time, route it through the designated process, and follow authorised identity-verification and response steps."
        },
        {
          "id": "c",
          "label": "Tell the applicant to resend the request using legal terminology.",
          "feedback": "A request can be valid without legal language or a special form. Routing it promptly is the appropriate action."
        }
      ]
    },
    "takeaway": {
      "kicker": "Remember",
      "title": "Spot it and route it",
      "body": "Requests to access, correct or delete information—and privacy complaints—may be informal. Record them accurately and route them promptly.",
      "bullets": [],
      "narration": "People do not need special wording to raise a privacy request. Recognise it, record it, route it and leave identity checks and outcome decisions to the authorised team."
    }
  },
  {
    "id": "ch12",
    "title": "Report mistakes and suspected incidents immediately",
    "blurb": "Report actual or suspected information incidents immediately, preserve useful details and take only authorised containment actions.",
    "visual": "desk_privacy",
    "slides": [
      {
        "kicker": "Introduction",
        "title": "Mistakes need fast, calm reporting",
        "body": "An information incident can involve confidentiality, accuracy or availability. Examples include a wrong-recipient message, lost device or paper, public link, inappropriate access, accidental deletion or unauthorised upload.",
        "bullets": [
          "Report promptly even if you are unsure or caused the mistake.",
          "Use the primary reporting channel or the documented fallback.",
          "Give clear facts so the response team can assess and contain the event."
        ],
        "narration": "Information incidents include more than hacking. A wrong recipient, lost document, public link, inappropriate access, accidental deletion or unapproved upload may all require action. Report promptly even when the impact is unclear or the mistake is yours. Early information gives the authorised response team more options to recall, restrict, recover, investigate and protect affected people."
      },
      {
        "kicker": "The risk",
        "title": "Delay can turn a fixable error into harm",
        "body": "Waiting may allow a link to be downloaded, a device to remain accessible or an incorrect record to spread. Quietly deleting evidence can make the incident harder to understand.",
        "bullets": [
          "People may lose control of private information or face fraud and distress.",
          "Customers may receive incorrect service or lose access to essential records.",
          "The organisation may lose time needed for containment, assessment and required notifications."
        ],
        "narration": "The behaviour to prevent is hiding, delaying or improvising. A report is not an admission that the worst has happened; it starts an assessment. Delay can increase exposure and reduce containment options. Deleting messages, wiping devices or changing records without direction may destroy evidence. Fast, factual reporting helps specialists determine what happened, who may be affected and which response is proportionate."
      },
      {
        "kicker": "Illustrative office example",
        "title": "Waiting to see what happened",
        "body": "Priya emails a complaint file to the wrong external address. She notices immediately but waits until the next morning to report it because the recipient may not open the message.",
        "bullets": [
          "The response team loses time to attempt recall and contact the recipient.",
          "The recipient downloads the attachment overnight.",
          "The affected customer cannot be assessed or supported promptly."
        ],
        "narration": "Priya sends a complaint file to the wrong external address and notices immediately. She waits until morning because the recipient might not open it. During that delay, the attachment is downloaded and the response team loses the chance to act earlier. Reporting at once would not guarantee recall, but it would allow authorised containment, risk assessment and support to begin while more options were available.",
        "art": "ch12-report"
      },
      {
        "kicker": "What to do",
        "title": "Report, preserve and follow direction",
        "body": "If something may have gone wrong:",
        "bullets": [
          "Report immediately through the approved channel; use the fallback contact if the normal system is unavailable.",
          "State what happened, when, which information and systems were involved, who may have received it and what you have already done.",
          "Preserve relevant messages, URLs, filenames and times.",
          "Take only approved containment steps. Do not wipe, alter, contact outsiders or investigate beyond your authority unless instructed."
        ],
        "narration": "Report immediately and provide facts: what happened, when you noticed, the information and systems involved, possible recipients, and any action already taken. Preserve relevant messages, URLs, filenames, screenshots and times where safe to do so. Follow the response team’s containment instructions. Do not wipe a device, alter records, contact external recipients or run your own investigation unless the approved procedure specifically directs you."
      }
    ],
    "decision": {
      "prompt": "You upload a confidential draft to an unapproved public file-conversion website, then realise the mistake. You close the page and delete your local copy. What should you do next?",
      "narration": "A confidential draft has been uploaded to an unapproved conversion service. Closing the page and deleting the local file do not establish what the provider retained. Decide how to preserve useful information and start authorised containment.",
      "options": [
        {
          "id": "a",
          "label": "Wait to see whether the document appears online before reporting it.",
          "feedback": "Waiting can reduce containment options. Suspected incidents should be reported even when exposure is uncertain."
        },
        {
          "id": "b",
          "label": "Report immediately, preserve the service details and follow authorised containment instructions.",
          "correct": true,
          "feedback": "Correct. Provide the URL, time, filename, information involved and actions already taken. The response team can assess provider retention and direct containment."
        },
        {
          "id": "c",
          "label": "Create several new uploads to test how the website stores files.",
          "feedback": "Further uploads could increase exposure and interfere with the investigation. Do not investigate beyond your authority."
        }
      ]
    },
    "takeaway": {
      "kicker": "Remember",
      "title": "Report first, then contain safely",
      "body": "If information may be exposed, altered, lost or unavailable, report immediately—even when you are unsure. Preserve facts and follow authorised instructions.",
      "bullets": [],
      "narration": "Prompt reporting protects people and keeps response options open. Report uncertainty as well as confirmed incidents, preserve useful details and take only approved containment actions."
    }
  }
];
function deckFor(chapter: ChapterSpec, index: number): SlideDeckConfig {
  return {
    deckLabel: `CHAPTER ${index + 1} OF ${CHAPTERS.length}`,
    title: chapter.title,
    hubLabel: "Overview",
    knowHowLabel: "Test your know-how",
    chapterIndex: index,
    chapters: CHAPTERS.map((item) => ({ id: item.id, label: item.title })),
    continueLabel: "Back to overview",
    voiceId: "coral",
    slides: chapter.slides.map((slide) => ({ ...slide, visual: chapter.visual, ...(slide.art ? { art: slide.art } : {}) })),
    decision: chapter.decision,
    takeaway: chapter.takeaway,
  };
}

const drill: DecisionDrillConfig = {
  title: "What do you do?",
  intro: "Six situations. Choose the action.",
  passRatio: 0.66,
  continueLabel: "Continue to the check",
  voiceId: "coral",
  passEventId: "data_drill_strong",
  failEventId: "data_drill_weak",
  rounds: [
  {
    "id": "r1",
    "prompt": "You collect your print job and find an extra page: a job applicant's passport scan with her photo, date of birth and passport number. What do you do?",
    "narration": "You walk to the shared printer for your own document and find one extra page. It is a job applicant's passport scan, with her photograph, date of birth and passport number. Choose what you do with that page.",
    "options": [
      {
        "id": "a",
        "label": "Leave it in the tray so whoever printed it can come back for it.",
        "feedback": "Everyone in the building walks past that printer. A passport number left face up is an identity theft kit."
      },
      {
        "id": "b",
        "label": "Take the page straight to HR and say where you found it.",
        "correct": true,
        "feedback": "You get it out of public view immediately and hand it to the people who are allowed to hold it."
      },
      {
        "id": "c",
        "label": "Put it face down in the recycling bin so nobody reads it.",
        "feedback": "Recycling is tipped into open sacks, and HR still needs to know an applicant's passport was printed and abandoned."
      }
    ]
  },
  {
    "id": "r2",
    "prompt": "IT gave you temporary access to the HR drive to fix a broken form. A folder called Disciplinary letters 2025 opens when you click it. What do you do?",
    "narration": "IT gave you temporary access to the HR drive so you could repair a broken form. You see a folder called Disciplinary letters 2025, and it opens when you click it. Choose what you do.",
    "options": [
      {
        "id": "a",
        "label": "Open one letter to confirm the access really is too wide before reporting it.",
        "feedback": "You do not need to read a colleague's disciplinary letter to report that the folder opens."
      },
      {
        "id": "b",
        "label": "Fix the form, open nothing else, and tell IT that the access also exposes disciplinary letters.",
        "correct": true,
        "feedback": "You do the job you were given and flag the extra access so it is narrowed before the next person gets it."
      },
      {
        "id": "c",
        "label": "Say nothing, because the access expires on Friday.",
        "feedback": "Expiry does not help the next person granted the same over-wide access."
      }
    ]
  },
  {
    "id": "r3",
    "prompt": "A customer asks you to resend their quote. The email chain you were about to forward contains another customer's bank account number eleven messages down. What do you send?",
    "narration": "A customer asks you to resend their quote. The quickest route is to forward the existing email chain. Eleven messages down that chain is a different customer's bank account number. Choose what you send.",
    "options": [
      {
        "id": "a",
        "label": "Forward the chain and ask them to ignore the earlier messages.",
        "feedback": "People scroll. You would be handing one customer another customer's bank account number."
      },
      {
        "id": "b",
        "label": "Start a fresh email with only the quote attached and send nothing from the old chain.",
        "correct": true,
        "feedback": "A new email carries exactly what they asked for and nothing that belongs to somebody else."
      },
      {
        "id": "c",
        "label": "Forward the chain after deleting the one message with the bank details.",
        "feedback": "Hand-trimming an eleven message chain is how details get missed. A fresh email is certain."
      }
    ]
  },
  {
    "id": "r4",
    "prompt": "An email from your supplier's accounts address says their bank account has changed and this month's 22,000 pound payment must go to a new one. What do you do?",
    "narration": "An email arrives from your regular supplier's usual accounts address. It says their bank account has changed and this month's twenty two thousand pound payment must go to the new one. Choose what you do before paying.",
    "options": [
      {
        "id": "a",
        "label": "Reply to the email and ask them to confirm the new account number.",
        "feedback": "If that mailbox is compromised, the attacker reads your question and confirms their own account."
      },
      {
        "id": "b",
        "label": "Call the supplier on the number from an old invoice and confirm the change with a person you know.",
        "correct": true,
        "feedback": "Using a number you already had, rather than one from the email, is the step an impersonator cannot fake."
      },
      {
        "id": "c",
        "label": "Send a small test payment first and pay the rest if it clears.",
        "feedback": "A test payment clears into the attacker's account perfectly. It proves the account works, not that it is theirs."
      }
    ]
  },
  {
    "id": "r5",
    "prompt": "You need figures from a scanned customer invoice. The invoice shows their bank details. The fastest option is a free online PDF-to-Excel site. What do you do?",
    "narration": "You need the numbers from a scanned customer invoice turned into a spreadsheet, and that invoice shows the customer's bank account details. The first search result is a free PDF to Excel converter. Choose what you do.",
    "options": [
      {
        "id": "a",
        "label": "Use the free site, then delete the file from it afterwards.",
        "feedback": "The upload already happened. You cannot know what that site kept, and the customer's bank details went with it."
      },
      {
        "id": "b",
        "label": "Type the figures in by hand, or use the conversion tool IT has approved.",
        "correct": true,
        "feedback": "A few minutes of typing keeps the customer's bank details inside systems we control."
      },
      {
        "id": "c",
        "label": "Black out the bank details in the PDF, then upload it.",
        "feedback": "A black box drawn on a PDF often leaves the text readable underneath, and the file still goes to a company we have no agreement with."
      }
    ]
  },
  {
    "id": "r6",
    "prompt": "You are sharing your whole screen with two suppliers when an email preview appears showing a customer's name and the words card declined again. What do you do?",
    "narration": "You are sharing your entire screen on a video call with two people from a supplier. An email notification slides in showing a customer's name and the words card declined again. Choose what you do.",
    "options": [
      {
        "id": "a",
        "label": "Carry on and hope nobody read it in the two seconds it was visible.",
        "feedback": "Two seconds is plenty of time to read a name, and if the call is being recorded it is captured permanently."
      },
      {
        "id": "b",
        "label": "Stop sharing, turn on do-not-disturb, then share only the single window you need.",
        "correct": true,
        "feedback": "Sharing one window instead of the whole screen means a notification cannot appear in front of guests."
      },
      {
        "id": "c",
        "label": "Keep sharing and drag the notification onto your second monitor.",
        "feedback": "The next notification arrives in the same place, and everyone watches you move this one."
      }
    ]
  }
],
};

const privacyHunt: PrivacyHuntConfig = {
  ...deviceConfig<PrivacyHuntConfig>("privacy_hunt"),
  headline: "Find the risks",
};

const privacyClassify = deviceConfig<PrivacyClassifyConfig>("privacy_classify");

const privacyIntro = beat("b_intro_privacy");
const privacyHuntBeat = beat("b_privacy_hunt");
const privacyClassifyBeat = beat("b_privacy_classify");
const privacySafe = beat("b_teach_privacy_safe");
const privacyRisky = beat("b_teach_privacy_risky");

const chapterEdges: ScenarioSpec["edges"] = [
  { from: "b_hub", to: "b_ch1_title", on: "play_all" },
];
CHAPTERS.forEach((chapter, index) => {
  const n = index + 1;
  const deck = `b_ch${n}_deck`;
  chapterEdges.push(
    { from: "b_hub", to: `b_ch${n}_title`, on: chapter.id },
    { from: `b_ch${n}_title`, to: `b_ch${n}_cam` },
    { from: `b_ch${n}_cam`, to: `b_ch${n}_tv` },
    { from: `b_ch${n}_tv`, to: deck },
    { from: deck, to: "b_return_hub" },
    { from: deck, to: "b_return_hub", on: "hub" },
  );
  if (index > 0) {
    chapterEdges.push({ from: deck, to: `b_ch${index}_deck`, on: "prev" });
  }
  if (index < CHAPTERS.length - 1) {
    chapterEdges.push({ from: deck, to: `b_ch${index + 2}_deck`, on: "next" });
  }
  CHAPTERS.forEach((other, otherIndex) => {
    if (otherIndex === index) return;
    chapterEdges.push({ from: deck, to: `b_ch${otherIndex + 1}_deck`, on: other.id });
  });
});

export const dataHandlingScenario: ScenarioSpec = {
  version: 1,
  id: "data-handling",
  title: "Handling company and customer data",
  summary: {
    goal: "Learn to recognise information that needs protection and handle it safely, from collection, storage and sharing to AI tools, disposal and reporting mistakes.",
    youDo: "Watch the film, pick chapters with narrated slides and a decision each, find and sort information left out in the lounge, then work through six short situations and a quiz.",
    duration: "Core 6-8 min, game and chapters optional (3-6 min each)",
    tags: ["data_protection", "file_sharing", "verification", "physical_security", "ai_use", "incident_reporting"],
    audience: ["all_staff"],
    needs: ["sound"],
  },
  topic: "Everyday decisions about company information, customer data and employee information",
  language: "en",
  ambientUrl: "/audio/gymnopedie-kevin-macleod.mp3",
  ambientVolume: 0.14,
  music: "none",
  riskMeter: false,
  exposureMap: true,
  passThreshold: 80,
  allowFreeExplore: false,

  scene: {
    environment: OFFICE_ENV,
    environments: [
      {
        id: "office",
        environment: OFFICE_ENV,
        ambientUrl: "/audio/gymnopedie-kevin-macleod.mp3",
        ambientVolume: 0.14,
        music: "none",
      },
    ],
    props: [],
    waypoints: [
      waypoint("wp_reception_wide"),
      waypoint("wp_terminal_close"),
      waypoint("wp_meeting_establish"),
      meetingTv,
      waypoint("wp_privacy_entrance"),
      waypoint("wp_privacy_lookonly"),
    ],
    initialWaypointId: "wp_reception_wide",
  },

  devices: [
    {
      id: "data_hub",
      kind: "breachbrief",
      config: {
        noticeTag: "DATA HANDLING",
        noticeKicker: "SECURITY AWARENESS BRIEFING",
        title: "Handling company and customer data",
        offLabel: "Switch on the welcome terminal",
        idleImage: "/cdn/sim/awareness-videos/handling-company-customer-data/wide/endcard.jpg",
        voiceId: "coral",
        progressKey: "data-handling",
        lines: [
          "This briefing is about everyday handling of company information, customer data and employee information, including documents, conversations, photographs and paper records.",
          "It follows information from collection, through use, sharing and storage, to disposal and handover.",
          "Your organisation may use its own classification names and approval routes. When those rules are stricter, follow them. Start with the office privacy game, or choose a chapter on the conference-room television.",
        ],
        hubIntro: "Learn what needs protection and how to handle it, then practise in the office.",
        exercisesKicker: "1 interactive game",
        exercisesLabel: "Practise in the office",
        hubVideo: {
          title: "Handling company and customer data",
          blurb: "A short briefing on recognising, sharing and protecting company and customer information.",
          src: "/cdn/sim/awareness-videos/handling-company-customer-data/handling-company-and-customer-data.mp4",
          poster: "/cdn/sim/awareness-videos/handling-company-customer-data/wide/presenter.jpg",
        },
        exercises: [
          {
            id: "game_privacy",
            label: "Office privacy",
            blurb: "Find information left in the open, then sort it.",
            key: "game_privacy",
            completionBeatId: "b_done_privacy",
            icon: "privacy",
            hint: "Find and sort",
            imageUrl: `${ART}/privacy.jpg`,
          },
        ],
        cardsTitle: "Learn the handling rules",
        cardsIntro: "Ten short chapters. Choose one, or play them all. On the television you can move to the next chapter or come back here.",
        cards: [
          {
            id: "play_all",
            label: "Play all chapters",
            blurb: "One sitting on the conference-room TV. You can return here between chapters.",
            key: "play_all",
            completionBeatId: `b_ch${CHAPTERS.length}_deck`,
            icon: "chapters",
            hint: `All ${CHAPTERS.length}`,
            wide: true,
            actionLabel: "Play all",
            doneLabel: "Played",
            imageUrl: `${ART}/play-all.jpg`,
          },
          ...CHAPTERS.map((chapter, index) => ({
            id: chapter.id,
            label: chapter.title,
            blurb: chapter.blurb,
            key: chapter.id,
            completionBeatId: `b_ch${index + 1}_deck`,
            icon: "slides" as const,
            hint: `Chapter ${index + 1}`,
            imageUrl: `${ART}/${chapter.id}.jpg`,
          })),
        ],
        finishLabel: "Finish the briefing",
        finishHint: "Continue to the decision drill",
        finishEarlyHint: "You can finish now, or explore the game and the chapters first.",
      },
    },
    ...CHAPTERS.map((chapter, index) => ({
      id: `deck_ch${index + 1}`,
      kind: "slidedeck" as const,
      config: deckFor(chapter, index),
    })),
    { id: "privacy_hunt", kind: "privacyhunt", config: privacyHunt },
    { id: "privacy_classify", kind: "privacyclassify", config: privacyClassify },
    { id: "decision_drill", kind: "decisiondrill", config: drill },
  ],

  voiceAttackers: {
    "data-handling": {
      persona: {
        name: "Instructor",
        role: "Security awareness",
        pretext: "it_support",
        language: "en",
        voiceId: "coral",
      },
      goal: "Help the employee handle company and customer information safely.",
      opening: "",
      allowedTopics: [],
      scoringEvents: [
        ...scoring(
          "privacy_hunt_thorough",
          "privacy_hunt_partial",
          "privacy_sorted_well",
          "privacy_sorted_poorly",
        ),
        {
          id: "data_drill_strong",
          label: "Chose the safer action in most of the decision drill",
          kind: "positive",
          weight: 15,
          dimension: "information",
          detail: "The safer action checked purpose, recipient, channel or approval before the information moved.",
        },
        {
          id: "data_drill_weak",
          label: "Missed several safer actions in the decision drill",
          kind: "risk",
          weight: 15,
          dimension: "information",
          detail: "Urgency, convenience or a missing label was treated as permission to move or keep the information.",
        },
      ],
    },
  },

  beats: [
    {
      id: "b_intro",
      type: "intro",
      title: "Handling company\nand customer data",
      kicker: "Staff briefing",
      icon: "shield",
      ctaLabel: "Open the briefing",
      guide: {
        title: "Handling company and customer data",
        body: "You start at the unattended reception of the office. The welcome terminal is your overview of handling company and customer data: a short film, ten narrated chapters on the conference-room television, and an office privacy game in the lounge. Switch the terminal on to begin.",
      },
    },
    { id: "b_cam_terminal", type: "camera", waypointId: "wp_terminal_close", walkSfx: true },
    {
      id: "b_hub",
      type: "device",
      deviceId: "data_hub",
      guide: { title: "Briefing overview", body: "Play the privacy game, choose a chapter, or play all of them." },
    },

    ...CHAPTERS.flatMap((chapter, index) => {
      const n = index + 1;
      const beats: Beat[] = [
        {
          id: `b_ch${n}_title`,
          type: "title",
          environmentId: "office",
          title: chapter.title,
          autoAdvanceMs: 1100,
          guide: { title: `Chapter ${n}`, body: "The presentation is on the conference-room television." },
        },
        { id: `b_ch${n}_cam`, type: "camera", waypointId: "wp_meeting_establish", walkSfx: true },
        { id: `b_ch${n}_tv`, type: "camera", waypointId: "wp_meeting_tv_close" },
        {
          id: `b_ch${n}_deck`,
          type: "device",
          deviceId: `deck_ch${n}`,
          guide: {
            title: chapter.title,
            body: "Move through the slides, try the decision, or jump to the next chapter. Overview takes you back to the terminal.",
          },
        },
      ];
      return beats;
    }),

    {
      id: "b_privacy_title",
      type: "title",
      environmentId: "office",
      title: "Office privacy",
      autoAdvanceMs: 1100,
      guide: { title: "Office privacy", body: "The lounge is where the information has been left out." },
    },
    { id: "b_cam_privacy", type: "camera", waypointId: "wp_privacy_entrance", walkSfx: true },
    {
      ...privacyIntro,
      kicker: "Office privacy",
      guide: {
        title: "Office privacy",
        body: "Look around the lounge. Click anything that might be information left in the open, on a screen, on paper or on a device.",
      },
    },
    {
      ...privacyHuntBeat,
      guide: {
        title: "Office privacy",
        body: "Click anything you can find. Each item tells you whether it should have been left out.",
        cta: "Click what you find, then continue.",
      },
    },
    {
      ...privacyClassifyBeat,
      guide: {
        title: "Sort what you found",
        body: "Drag each card into the level it belongs to.",
      },
    },
    teachReturn(privacySafe),
    teachReturn(privacyRisky),
    {
      id: "b_done_privacy",
      type: "title",
      title: "Office privacy complete",
      autoAdvanceMs: 850,
      guide: { title: "Complete", body: "Returning to the briefing overview." },
    },

    { id: "b_return_hub", type: "camera", environmentId: "office", waypointId: "wp_terminal_close", walkSfx: true },
    {
      id: "b_drill",
      type: "device",
      deviceId: "decision_drill",
      guide: { title: "Decision drill", body: "Choose the action you would take. You get a short explanation after each one." },
    },
    {
      id: "b_quiz",
      type: "quiz",
      title: "Check your decisions",
      quiz: [
        {
          id: "q_collect",
          title: "Question 1",
          prompt: "A form asks for a customer's date of birth. The service does not use it. What do you do?",
          options: [
            { id: "a", label: "Collect it in case a later team wants it", icon: "x" },
            { id: "b", label: "Leave it out and ask whether the field should be on the form", correct: true, icon: "check" },
            { id: "c", label: "Collect it if the customer does not complain", icon: "alert" },
          ],
          rationale: "Collect the minimum the work needs. A field the service does not use should not be filled in.",
        },
        {
          id: "q_access",
          title: "Question 2",
          prompt: "You can open a folder of everyone's salary, and your work does not need it. What do you do?",
          options: [
            { id: "a", label: "Open a few files so you understand the risk", icon: "x" },
            { id: "b", label: "Close it and report that your access is too wide", correct: true, icon: "lock" },
            { id: "c", label: "Leave it open in case you need it later", icon: "alert" },
          ],
          rationale: "Being able to open a record is not authorisation to use it. Excessive access gets reported.",
        },
        {
          id: "q_store",
          title: "Question 3",
          prompt: "You want to finish a customer proposal on the train by emailing it to your personal account. What do you do?",
          options: [
            { id: "a", label: "Send it and delete it tonight", icon: "x" },
            { id: "b", label: "Use the approved work method, or wait until you can", correct: true, icon: "check" },
            { id: "c", label: "Copy it to a USB stick instead", icon: "alert" },
          ],
          rationale: "Personal email and an unapproved USB stick are both unapproved places for the proposal.",
        },
        {
          id: "q_verify",
          title: "Question 4",
          prompt: "A chat message from your director demands a customer export in the next ten minutes. What do you do?",
          options: [
            { id: "a", label: "Send it so the deadline is met", icon: "clock" },
            { id: "b", label: "Verify the person and the approval through a channel you already trust", correct: true, icon: "check" },
            { id: "c", label: "Send it if the display name looks right", icon: "x" },
          ],
          rationale: "Urgency and a familiar name are reasons to verify on a known channel, not reasons to skip the check.",
        },
        {
          id: "q_records",
          title: "Question 5",
          prompt: "Two copies of a customer record disagree about a payment. What do you do?",
          options: [
            { id: "a", label: "Keep both so neither version is lost", icon: "x" },
            { id: "b", label: "Use the authoritative source and correct the other copy properly", correct: true, icon: "check" },
            { id: "c", label: "Pick the copy that was edited most recently", icon: "alert" },
          ],
          rationale: "One source of truth should drive the decision. The other copy gets corrected through the proper process.",
        },
      ],
    },
  ],

  edges: [
    { from: "b_intro", to: "b_cam_terminal" },
    { from: "b_cam_terminal", to: "b_hub" },
    { from: "b_hub", to: "b_drill", on: "finish" },
    { from: "b_hub", to: "b_privacy_title", on: "game_privacy" },
    ...chapterEdges,
    { from: "b_privacy_title", to: "b_cam_privacy" },
    { from: "b_cam_privacy", to: "b_intro_privacy" },
    { from: "b_intro_privacy", to: "b_privacy_hunt" },
    { from: "b_privacy_hunt", to: "b_privacy_classify" },
    { from: "b_privacy_classify", to: "b_teach_privacy_safe", on: "safe" },
    { from: "b_privacy_classify", to: "b_teach_privacy_risky", on: "risky" },
    { from: "b_teach_privacy_safe", to: "b_done_privacy" },
    { from: "b_teach_privacy_risky", to: "b_done_privacy" },
    { from: "b_done_privacy", to: "b_return_hub" },
    { from: "b_return_hub", to: "b_hub" },
    { from: "b_drill", to: "b_quiz" },
  ],

  initialBeatId: "b_intro",
};
